import React, { createRef, useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Link, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Notification from "../entities/notification/Notification";
import AdminOnly from "../AdminOnly";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import AuditSidebar from "../audit-sidebar/AuditSidebar";
import API_ROUTES from "../../api-routes";
import { Skeleton } from "primereact/skeleton";
import useDateTime from "~/hooks/useDateTime";

const DirectorsTable = ({
  directorsArr,
  onDirectorClick,
  editPopup,
  setEditPopup,
  loading,
  setLoading,
}) => {
  const navigate = useNavigate();
  const { id: entityId } = useParams();
  const { formatDateTime } = useDateTime();

  const [selectedDirector, setSelectedDirector] = useState(null);
  const contextMenu = createRef();
  const [isAuditSidebarOpen, setIsAuditSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const auditLabels = () => {
    return {
      create: "Director Created",
      update: "Director Updated",
      delete: "Director Deleted",
    };
  };

  const formatDate = (date) => {
    if (!date) return null;
    const parsedDate = new Date(date);
    if (parsedDate.toString() === "Invalid Date") return null;
    return parsedDate.toLocaleDateString("en-GB");
  };

  const handleEditClick = (id) => {
    const selectedDirectorData = directorsArr.find(
      (director) => director.id === id,
    );
    if (selectedDirectorData) {
      setSelectedDirector(selectedDirectorData);
    }
  };

  useEffect(() => {
    if (selectedDirector) {
      const id = selectedDirector.id;
      navigate(`/${entityId}/directors/${id}/edit`, {
        state: { rowData: selectedDirector },
      });
    }
  }, [selectedDirector, navigate]);

  const actionBodyTemplate = (rowData) => {
    return (
      <AdminOnly>
        <div className="flex align-items-center">
          <span
            className="pi pi-pencil ml-4 mr-3 pencil-pointer"
            onClick={() => handleEditClick(rowData.id)}
          ></span>
        </div>
      </AdminOnly>
    );
  };

  const statusTemplate = (rowData) => {
    const renderStatus = (status) => {
      switch (status) {
        case "Active":
          return (
            <>
              <span className="pi pi-circle-fill active-circle-fill"></span>{" "}
              Active
            </>
          );
        case "Not Active":
          return (
            <>
              <span className="pi pi-circle-fill not-active-circle-fill"></span>{" "}
              Not Active
            </>
          );
        case "Removed":
          return (
            <>
              <span className="pi pi-circle-fill removed-circle-fill"></span>{" "}
              Removed
            </>
          );
        default:
          return "-";
      }
    };

    return renderStatus(rowData.status);
  };
  const nameTemplate = (rowData) => {
    return (
      <Link className="link-item" to={`/directors/${rowData.id}`}>
        {rowData?.preferred_name ||
          [rowData.name, rowData.other_name, rowData.last_name]
            .filter(Boolean)
            .join(" ")}
        {rowData.positions?.length > 1 && <div></div>}
      </Link>
    );
  };

  useEffect(() => {
    if (selectedDirector) {
      localStorage.setItem("directorId", selectedDirector.id);
    }
  }, [selectedDirector]);

  const flattenDirectorsData = (directors = []) => {
    if (!Array.isArray(directors)) return [];

    return directors.reduce((acc, director) => {
      const positionsArray = (director.positions || []).map((position) => ({
        id: director.id,
        name:
          director.preferred_name ||
          [director.name, director.other_name, director.last_name]
            .filter(Boolean)
            .join(" "),
        position: position.position,
        start_date: position.start_date,
        end_date: position.end_date,
        status: position.status,
      }));

      return acc.concat(positionsArray);
    }, []);
  };

  const directorsFlattenedArr = flattenDirectorsData(directorsArr);
  const filteredDirectors = directorsFlattenedArr.filter((row) => {
    const fullName = row.name?.toLowerCase() || "";
    const position = row.position?.toLowerCase() || "";
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || position.includes(search);
  });

  useEffect(() => {
    if (directorsFlattenedArr) {
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [directorsFlattenedArr]);
  const uniqueIdsCount = useMemo(() => {
    return new Set(filteredDirectors.map((item) => item.id)).size;
  }, [filteredDirectors]);

  return (
    <>
      <div className="formgrid grid mt-4">
        <div className="field col-6 mb-4 pl-0">
          <div className="input-wrapper ml-2">
            <span className="pi pi-search span-left"></span>
            <input
              type="text"
              placeholder="Search directors by given name, surname or position"
              className="input-field appearance-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="pi pi-search span-right"></span>
          </div>
        </div>
        <div className="field col-6 mb-0">
          <div className="flex justify-content-end">
            {/* <button className="cancel-btns mr-3">
              Filters<span className="pi pi-filter mb-1 ml-2"></span>
            </button> */}
            <AdminOnly>
              <button className="add-new-contact" onClick={onDirectorClick}>
                Add Board Member <span className="pi pi-plus ml-2"></span>
              </button>
              <Menu
                className="global-entities-context-menu"
                popup
                ref={contextMenu}
                id="context_menu"
                model={[
                  {
                    label: (
                      <div className="global-entities-context-menu-item">
                        View History
                        <i className="pi pi-history" />
                      </div>
                    ),
                    command: () => setIsAuditSidebarOpen(true),
                  },
                ]}
              />
              <Button
                onClick={(event) => contextMenu.current.toggle(event)}
                icon="pi pi-ellipsis-v"
                outlined
                severity="success"
              />
            </AdminOnly>
          </div>
        </div>
      </div>

      <Notification editPopup={editPopup} setEditPopup={setEditPopup} />
      <Card className="gen-card h-full">
        <div className="formgrid grid">
          <div className="field col-12 mt-2">
            <div className="card">
              {!loading && directorsFlattenedArr.length > 0 ? (
                <DataTable
                  value={filteredDirectors}
                  rowGroupMode="rowspan"
                  groupRowsBy="id"
                  sortMode="multiple"
                  tableStyle={{ minWidth: "50rem" }}
                  paginator={uniqueIdsCount > 10}
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                  rows={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  className="datatable-right-align"
                  emptyMessage="No results were found"
                >
                  <Column
                    field="id"
                    header="Full Name"
                    sortable
                    rowspan
                    style={{ minWidth: "200px" }}
                    body={nameTemplate}
                  />

                  <Column
                    field="position"
                    header="Position"
                    style={{ minWidth: "200px" }}
                  />

                  <Column
                    field="start_date"
                    header="Start Date"
                    style={{ minWidth: "150px" }}
                    body={(rowData) =>
                      formatDateTime({
                        date: rowData.start_date,
                        dateformat: "MMMM do, yyyy",
                      }) || "-"
                    }
                  />

                  <Column
                    field="end_date"
                    header="End Date"
                    style={{ minWidth: "150px" }}
                    body={(rowData) =>
                      formatDateTime({
                        date: rowData.end_date,
                        dateformat: "MMMM do, yyyy",
                      }) || "-"
                    }
                  />

                  <Column
                    field="status"
                    header="Status"
                    style={{ minWidth: "100px" }}
                    body={statusTemplate}
                  />

                  <Column
                    body={actionBodyTemplate}
                    className="directors-col-width"
                  />
                </DataTable>
              ) : (
                <DataTable
                  value={Array(10).fill({})}
                  tableStyle={{ minWidth: "100%" }}
                  className="datatable-right-align"
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 20, 30]}
                >
                  <Column
                    header="Full Name"
                    sortable
                    className="table-col-width"
                    body={<Skeleton />}
                  />
                  <Column
                    header="Position"
                    className="table-col-width"
                    body={<Skeleton />}
                  />
                  <Column
                    header="Start Date"
                    className="table-col-width"
                    body={<Skeleton />}
                  />
                  <Column
                    header="End Date"
                    className="table-col-width"
                    body={<Skeleton />}
                  />
                  <Column
                    header="Status"
                    className="table-col-width"
                    body={<Skeleton />}
                  />
                  <Column className="table-col-width" body={<Skeleton />} />
                </DataTable>
              )}
            </div>
          </div>
        </div>
      </Card>
      {isAuditSidebarOpen && (
        <AuditSidebar
          heading="Directors"
          endpoint={API_ROUTES.FETCH_DIRECTORS_AUDIT}
          id={entityId}
          setSidebarToggle={setIsAuditSidebarOpen}
          sidebarToggle={isAuditSidebarOpen}
          labels={auditLabels}
        />
      )}
    </>
  );
};

export default DirectorsTable;
